import { readAllBytes } from './client.ts';
import type { AppUpdateStatus, Bridge, WsMessage } from './types.ts';

/**
 * The bridge conformance suite (proposal §6.4): the same cases against every host, through the
 * `Bridge` interface only. The peers are the servers of `@casa/bridge-node`'s
 * `startConformanceServer` (run by `scripts/conformance-server.mjs` for the shells). A host passes
 * when every case passes.
 *
 * Not covered here: lifecycle events (they need the app to be sent to the background by hand) and
 * the origin lock (it needs a page from another origin); see the iteration plan, C1.
 */

// Not part of the ES2023 lib, but present in every runtime the suite runs in.
declare const TextEncoder: new () => { encode(text: string) : Uint8Array };
declare const TextDecoder: new () => { decode(bytes: Uint8Array) : string };
declare const setTimeout: (callback: () => void, ms: number) => unknown;
declare const clearTimeout: (timer: unknown) => void;
declare const performance: { now() : number };

export interface ConformanceTarget {
    /** `http://host:port` of the HTTP peer. */
    httpBase: string;
    /** `ws://host:port/ws` of the WebSocket echo. */
    wsUrl: string;
    /**
     * The HTTP peer's routes over HTTPS, with a certificate made like a Hue bridge's (hue-driver HU-9):
     * signed by `ca`, its name `serverName` in the CN only; `otherCa` signed nothing it shows.
     */
    https: { base: string; ca: string; otherCa: string; serverName: string };
    tcp: { host: string; port: number };
    /** A port on the same host where nothing listens. */
    refusedPort: number;
}

export interface ConformanceOptions {
    /** Length of the TCP soak. Default 5 s. */
    soakSeconds?: number;
    /** Rate of the TCP soak. Default 1 MB/s. */
    soakBytesPerSecond?: number;
    /** Run only the cases whose name starts with one of these. */
    only?: readonly string[];
}

export interface ConformanceResult {
    name: string;
    ok: boolean;
    ms: number;
    /** What failed, or a measurement worth reporting (the soak's throughput). */
    detail?: string;
}

type Case = [name: string, run: () => Promise<string | void>];

const STORAGE_PREFIX = 'conformance/';
const SECRET = 'conformance-secret-7f3a';

function check(condition: boolean, message: string) : asserts condition {
    if (!condition) {
        throw new Error(message);
    }
}

function patternBytes(length: number) : Uint8Array {
    return new Uint8Array(length).map((_, i) => i % 251);
}

function isPattern(bytes: Uint8Array) : boolean {
    return bytes.every((byte, i) => byte === i % 251);
}

function withTimeout<T>(promise: Promise<T>, ms: number, what: string) : Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(`${what}: no result after ${ms} ms`)), ms);
        promise.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (error: unknown) => {
                clearTimeout(timer);
                reject(error);
            },
        );
    });
}

async function rejects(promise: Promise<unknown>, what: string) : Promise<string> {
    try {
        await promise;
    } catch (error) {
        return error instanceof Error ? error.message : String(error);
    }
    throw new Error(`${what} succeeded, but should have failed`);
}

function cases(bridge: Bridge, target: ConformanceTarget, options: ConformanceOptions) : Case[] {
    const { http, ws, tcp } = bridge.net;
    const text = (bytes: Uint8Array) : string => new TextDecoder().decode(bytes);
    const refusedHost = target.tcp.host;

    return [
        [
            'http: method, headers and body are sent; status and headers come back',
            async () => {
                const sent = patternBytes(70_000);
                const response = await http.request({ method: 'PUT', url: `${target.httpBase}/echo`, headers: { 'X-Tilez': 'one' }, body: sent });
                const echo = JSON.parse(text(await readAllBytes(response.body))) as { method: string; headers: Record<string, string>; body: string };
                check(response.status === 200, `status ${response.status}`);
                check(response.headers['x-conformance'] === 'yes', 'response header missing');
                check(echo.method === 'PUT', `method ${echo.method}`);
                check(echo.headers['x-tilez'] === 'one', 'request header missing');
                check(echo.body.length === Math.ceil(sent.length / 3) * 4, 'request body changed');
            },
        ],
        [
            // Drivers read them (Gigya's for Uconnect, EVCC's): fetch gives each on its own, URLSession and
            // HttpURLConnection join them; a host must not keep only the last.
            'http: every Set-Cookie comes back, joined in one header',
            async () => {
                const response = await http.request({ method: 'GET', url: `${target.httpBase}/cookies` });
                await readAllBytes(response.body);
                const cookies = response.headers['set-cookie'] ?? '';
                check(/(^|,\s*)one=1/.test(cookies) && /(^|,\s*)two=2/.test(cookies), `set-cookie ${JSON.stringify(cookies)}`);
            },
        ],
        [
            'http: an error status is a response, with its body',
            async () => {
                const response = await http.request({ method: 'GET', url: `${target.httpBase}/status?code=404` });
                check(response.status === 404, `status ${response.status}`);
                check(text(await readAllBytes(response.body)) === 'status 404', 'body');
            },
        ],
        [
            'http: redirects are followed',
            async () => {
                const response = await http.request({ method: 'GET', url: `${target.httpBase}/redirect` });
                await readAllBytes(response.body);
                check(response.status === 200, `status ${response.status}`);
            },
        ],
        [
            'http: a redirect to an app\'s scheme is the response when not to be followed',
            async () => {
                const response = await http.request({ method: 'GET', url: `${target.httpBase}/redirect-app`, redirect: 'manual' });
                await readAllBytes(response.body);
                check(response.status === 302, `status ${response.status}`);
                check(response.headers.location === 'casa-test://login?code=abc', `location ${response.headers.location}`);
            },
        ],
        [
            // A LAN device's own certificate (a Hue bridge's): its authority, and its name in the CN only.
            'https: a device is trusted with its own authority and name',
            async () => {
                const { base, ca, serverName } = target.https;
                const response = await http.request({ method: 'GET', url: `${base}/status?code=200`, tls: { ca: [ca], serverName } });
                check(text(await readAllBytes(response.body)) === 'status 200', `status ${response.status}`);
                const upper = await http.request({ method: 'GET', url: `${base}/status?code=200`, tls: { ca: [ca], serverName: serverName.toUpperCase() } });
                await readAllBytes(upper.body);
                check(upper.status === 200, `in capitals: status ${upper.status}`);
            },
        ],
        [
            'https: a device is trusted with its own authority and any name, when asked',
            async () => {
                const { base, ca } = target.https;
                const response = await http.request({ method: 'GET', url: `${base}/status?code=200`, tls: { ca: [ca], anyName: true } });
                await readAllBytes(response.body);
                check(response.status === 200, `status ${response.status}`);
            },
        ],
        [
            'https: a device is refused under another name, by the URL\'s host, or by another authority',
            async () => {
                const { base, ca, otherCa, serverName } = target.https;
                const url = `${base}/status?code=200`;
                await rejects(http.request({ method: 'GET', url, tls: { ca: [ca], serverName: `not-${serverName}` } }), 'another name');
                await rejects(http.request({ method: 'GET', url, tls: { ca: [ca] } }), 'the URL\'s host as the name');
                await rejects(http.request({ method: 'GET', url, tls: { ca: [otherCa], serverName } }), 'another authority');
            },
        ],
        [
            'https: a device\'s own certificate is refused without its authority',
            async () => {
                await rejects(http.request({ method: 'GET', url: `${target.https.base}/status?code=200` }), 'the system\'s authorities');
            },
        ],
        [
            'http: tls is refused on a plain http: request',
            async () => {
                const { ca, serverName } = target.https;
                await rejects(http.request({ method: 'GET', url: `${target.httpBase}/status?code=200`, tls: { ca: [ca], serverName } }), 'tls on http:');
            },
        ],
        [
            'http: a 2 MB body arrives intact',
            async () => {
                const response = await http.request({ method: 'GET', url: `${target.httpBase}/bytes?length=2000000` });
                const body = await readAllBytes(response.body);
                check(body.length === 2_000_000, `length ${body.length}`);
                check(isPattern(body), 'bytes changed');
            },
        ],
        [
            'http: a stalled body is cut off at the timeout',
            async () => {
                // The peer sends its head and 1 kB before it stalls, so the response must arrive on
                // every host (the iOS allowance is for a stall before any body) and only the body may
                // fail, and not before the timeout has run: any other error fails the case.
                const started = performance.now();
                const response = await withTimeout(http.request({ method: 'GET', url: `${target.httpBase}/stall`, timeoutMs: 1000 }), 10_000, 'stalled request');
                check(response.status === 200, `status ${response.status}`);
                const message = await withTimeout(rejects(readAllBytes(response.body), 'stalled body'), 10_000, 'stalled body');
                const elapsed = performance.now() - started;
                check(elapsed >= 900, `cut off after ${Math.round(elapsed)} ms, before the 1000 ms timeout: ${message}`);
                check(elapsed < 4000, `cut off only after ${Math.round(elapsed)} ms`);

                return message;
            },
        ],
        [
            'http: a reader that stops early cancels the request',
            async () => {
                const response = await http.request({ method: 'GET', url: `${target.httpBase}/stall` });
                for await (const chunk of response.body) {
                    check(chunk.length > 0, 'empty chunk');
                    break;
                }
            },
        ],
        [
            'http: a refused connection fails the request',
            async () => {
                // A short timeout: a firewall may drop the connection instead of refusing it.
                await withTimeout(
                    rejects(http.request({ method: 'GET', url: `http://${refusedHost}:${target.refusedPort}/`, timeoutMs: 5000 }), 'request'),
                    15_000,
                    'refused request',
                );
            },
        ],
        [
            'http: invalid requests are refused',
            async () => {
                await rejects(http.request({ method: 'GET', url: 'file:///etc/passwd' }), 'file: URL');
                await rejects(http.request({ method: 'G E T', url: `${target.httpBase}/echo` }), 'bad method');
            },
        ],
        [
            'http: credentials do not appear in errors',
            async () => {
                const message = await withTimeout(
                    rejects(
                        http.request({
                            method: 'GET',
                            url: `http://user:${SECRET}@${refusedHost}:${target.refusedPort}/`,
                            headers: { Authorization: `Basic ${SECRET}` },
                            timeoutMs: 5000,
                        }),
                        'request',
                    ),
                    15_000,
                    'request with credentials',
                );
                check(!message.includes(SECRET), `the error shows the credential: ${message}`);
            },
        ],
        [
            'ws: text and binary messages are echoed; the chosen subprotocol is reported',
            async () => {
                const socket = await ws.open(target.wsUrl, { protocols: ['casa.test'] });
                check(socket.protocol === 'casa.test', `protocol ${JSON.stringify(socket.protocol)}`);
                const received: WsMessage[] = [];
                const twoMessages = new Promise<void>((resolve) => {
                    socket.onMessage((message) => {
                        received.push(message);
                        if (received.length === 2) {
                            resolve();
                        }
                    });
                });
                const binary = patternBytes(300_000);
                await socket.send('héllo');
                await socket.send(binary);
                await withTimeout(twoMessages, 10_000, 'echo');
                check(received[0] === 'héllo', `text ${String(received[0])}`);
                const echoed = received[1];
                check(echoed instanceof Uint8Array && echoed.length === binary.length && isPattern(echoed), 'binary changed');
                await socket.close();
            },
        ],
        [
            'ws: a close by the server reports its code and reason',
            async () => {
                const socket = await ws.open(target.wsUrl);
                const reason = new Promise<string | undefined>((resolve) => socket.onClose(resolve));
                await socket.send('close-me');
                const closeReason = await withTimeout(reason, 10_000, 'server close');
                check(closeReason?.startsWith('4000') === true, `reason ${String(closeReason)}`);
            },
        ],
        [
            'ws: a refused connection fails the open',
            async () => {
                await withTimeout(rejects(ws.open(`ws://${refusedHost}:${target.refusedPort}/ws`), 'open'), 15_000, 'refused open');
            },
        ],
        [
            'tcp: data larger than one frame is echoed intact',
            async () => {
                const socket = await tcp.open(target.tcp.host, target.tcp.port);
                const sent = patternBytes(300_000);
                const received: Uint8Array[] = [];
                let length = 0;
                const all = new Promise<void>((resolve) => {
                    socket.onData((data) => {
                        received.push(data);
                        length += data.length;
                        if (length >= sent.length) {
                            resolve();
                        }
                    });
                });
                await socket.send(sent);
                await withTimeout(all, 10_000, 'echo');
                const joined = new Uint8Array(length);
                let offset = 0;
                for (const chunk of received) {
                    joined.set(chunk, offset);
                    offset += chunk.length;
                }
                check(length === sent.length && isPattern(joined), 'bytes changed');
                await socket.close();
            },
        ],
        [
            'tcp: soak',
            async () => {
                const seconds = options.soakSeconds ?? 5;
                const rate = options.soakBytesPerSecond ?? 1_000_000;
                const block = patternBytes(rate / 10);
                const socket = await tcp.open(target.tcp.host, target.tcp.port);
                let receivedBytes = 0;
                let corrupt = false;
                socket.onData((data) => {
                    for (const byte of data) {
                        if (byte !== (receivedBytes % block.length) % 251) {
                            corrupt = true;
                        }
                        receivedBytes++;
                    }
                });
                const started = performance.now();
                let sentBytes = 0;
                for (let i = 0; i < seconds * 10; i++) {
                    await socket.send(block);
                    sentBytes += block.length;
                    const ahead = started + (i + 1) * 100 - performance.now();
                    if (ahead > 0) {
                        await new Promise<void>((resolve) => setTimeout(resolve, ahead));
                    }
                }
                const sendSeconds = (performance.now() - started) / 1000;
                const backlogAtEnd = sentBytes - receivedBytes;
                const drainStarted = performance.now();
                const deadline = drainStarted + 10_000;
                while (receivedBytes < sentBytes && performance.now() < deadline) {
                    await new Promise<void>((resolve) => setTimeout(resolve, 10));
                }
                const drainMs = Math.round(performance.now() - drainStarted);
                await socket.close();
                check(!corrupt, 'bytes changed');
                check(receivedBytes === sentBytes, `${receivedBytes} of ${sentBytes} bytes came back`);

                return `${(sentBytes / 1e6).toFixed(1)} MB sent in ${sendSeconds.toFixed(1)} s (${(sentBytes / 1e6 / sendSeconds).toFixed(2)} MB/s); ${(backlogAtEnd / 1e3).toFixed(0)} kB still on the way at the end, back after ${drainMs} ms`;
            },
        ],
        [
            'storage: write, read, list, overwrite, remove',
            async () => {
                const { storage } = bridge;
                // What an earlier, interrupted run left behind.
                for (const path of await storage.list(STORAGE_PREFIX)) {
                    await storage.remove(path);
                }
                const bytes = patternBytes(100_000);
                await storage.write(`${STORAGE_PREFIX}a/one.bin`, bytes);
                await storage.write(`${STORAGE_PREFIX}two.json`, new TextEncoder().encode('{"v":1}'));
                const read = await storage.read(`${STORAGE_PREFIX}a/one.bin`);
                check(read !== null && read.length === bytes.length && isPattern(read), 'read back changed');
                const listed = await storage.list(STORAGE_PREFIX);
                check(JSON.stringify(listed) === JSON.stringify([`${STORAGE_PREFIX}a/one.bin`, `${STORAGE_PREFIX}two.json`]), `list ${JSON.stringify(listed)}`);
                await storage.write(`${STORAGE_PREFIX}two.json`, new TextEncoder().encode('{"v":2}'));
                check(text((await storage.read(`${STORAGE_PREFIX}two.json`)) ?? new Uint8Array()) === '{"v":2}', 'overwrite');
                for (const path of listed) {
                    await storage.remove(path);
                }
                check((await storage.list(STORAGE_PREFIX)).length === 0, 'files left after remove');
                check((await storage.read(`${STORAGE_PREFIX}missing.json`)) === null, 'a missing file reads as something');
                await storage.remove(`${STORAGE_PREFIX}missing.json`);
            },
        ],
        [
            'storage: paths outside the storage root are refused',
            async () => {
                for (const path of ['../escape', '/abs', 'a/../../b']) {
                    await rejects(bridge.storage.write(path, new Uint8Array([1])), `write to ${path}`);
                }
            },
        ],
        [
            'device: info',
            async () => {
                const info = await bridge.device.info();
                check([info.platform, info.osVersion, info.model].every((value) => typeof value === 'string' && value.length > 0), JSON.stringify(info));

                return `${info.platform} ${info.osVersion}, ${info.model}`;
            },
        ],
        [
            // A host with updates answers a whole status (app-updates AU-27); the Tilez Hub and the
            // shells that do not have them yet refuse. Nothing is checked, applied or confirmed here.
            'app: the update status is whole, or the host has no updates',
            async () => {
                let status: AppUpdateStatus;
                try {
                    status = await bridge.app.updateStatus();
                } catch (error) {
                    const message = error instanceof Error ? error.message : String(error);
                    check(/not available|invalid request/.test(message), message);

                    return `no updates in this host (${message})`;
                }

                const { active, pending } = status;
                check(typeof status.enabled === 'boolean' && (status.channel === 'test' || status.channel === 'stable'), JSON.stringify(status));
                check(Number.isSafeInteger(active.build) && typeof active.version === 'string' && (active.source === 'built-in' || active.source === 'downloaded'), JSON.stringify(active));
                check(pending === undefined || (Number.isSafeInteger(pending.build) && pending.build > active.build), JSON.stringify(pending));

                return `build ${active.build} (${active.source}), ${status.channel}${status.enabled ? '' : ', updates off'}`;
            },
        ],
    ];
}

/** Runs the suite; never throws, every case gets a result. */
export async function runConformance(
    bridge: Bridge,
    target: ConformanceTarget,
    options: ConformanceOptions = {},
    onResult?: (result: ConformanceResult) => void,
) : Promise<ConformanceResult[]> {
    const results: ConformanceResult[] = [];
    for (const [name, run] of cases(bridge, target, options)) {
        if (options.only && !options.only.some((prefix) => name.startsWith(prefix))) {
            continue;
        }

        const started = performance.now();
        let result: ConformanceResult;
        try {
            const detail = await withTimeout(run(), 120_000, name);
            result = { name, ok: true, ms: Math.round(performance.now() - started), ...(detail ? { detail } : {}) };
        } catch (error) {
            result = { name, ok: false, ms: Math.round(performance.now() - started), detail: error instanceof Error ? error.message : String(error) };
        }
        results.push(result);
        onResult?.(result);
    }

    return results;
}
